import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface Bookmark {
  key: string
  url: string
  name: string
}

interface BookmarksState {
  bookmarks: Bookmark[]
  addBookmark: (bookmark: Bookmark) => void
  removeBookmark: (key: string, url: string) => void
  toggleBookmark: (bookmark: Bookmark) => void
  isBookmarked: (key: string, url: string) => boolean
  getBookmarkByKeyAndUrl: (key: string, url: string) => Bookmark | undefined
  getBookmarksByKey: (key: string) => Bookmark[]
  clearBookmarks: () => void
}

export const useBookmarks = create<BookmarksState>()(
  persist(
    (set, get) => ({
      bookmarks: [],

      addBookmark: (bookmark: Bookmark) => {
        set((state) => ({
          bookmarks: [
            ...state.bookmarks.filter((b) => !(b.key === bookmark.key && b.url === bookmark.url)),
            bookmark,
          ],
        }))
      },

      removeBookmark: (key: string, url: string) => {
        set((state) => ({
          bookmarks: state.bookmarks.filter((b) => !(b.key === key && b.url === url)),
        }))
      },

      toggleBookmark: (bookmark: Bookmark) => {
        const { bookmarks } = get()
        const exists = bookmarks.some((b) => b.key === bookmark.key && b.url === bookmark.url)

        if (exists) {
          get().removeBookmark(bookmark.key, bookmark.url)
        } else {
          get().addBookmark(bookmark)
        }
      },

      isBookmarked: (key: string, url: string) => {
        const { bookmarks } = get()
        return bookmarks.some((b) => b.key === key && b.url === url)
      },

      getBookmarkByKeyAndUrl: (key: string, url: string) => {
        const { bookmarks } = get()
        return bookmarks.find((b) => b.key === key && b.url === url)
      },

      getBookmarksByKey: (key: string) => {
        const { bookmarks } = get()
        return bookmarks.filter((b) => b.key === key)
      },

      clearBookmarks: () => {
        set({ bookmarks: [] })
      },
    }),
    {
      name: 'bookmarks-storage',
      version: 1,
    },
  ),
)
